import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup, Typography } from '@mui/material';
import {
  ProgramWizardCurrentProgram,
  ProgramWizardCurrentProgramId,
  ProgramWizardOperatingMode,
  ProgramWizardStep,
  ProgramWizardStepValue,
} from '../ProgramWizard.types';
import WizardRadioButton from '../WizardRadioButton';

export type ProgramOption = {
  key: string;
  text: string;
  disabled?: boolean;
  nextStep: ProgramWizardStepValue;
  operatingMode?: ProgramWizardOperatingMode;
};

type StepSelectProgramProps = {
  currentProgram?: ProgramWizardCurrentProgram | null;
  selected: ProgramOption | null;
  onSelect: (option: ProgramOption) => void;
  isMobile?: boolean;
};

const StepSelectProgram = ({ currentProgram, selected, onSelect, isMobile = false }: StepSelectProgramProps) => {
  const { t } = useTranslation('programWizard');

  const options: ProgramOption[] = [
    {
      key: ProgramWizardCurrentProgramId.STANDBY,
      text: t('program.standby'),
      disabled: currentProgram?.id === ProgramWizardCurrentProgramId.STANDBY,
      nextStep: ProgramWizardStep.CONFIRM,
      operatingMode: ProgramWizardOperatingMode.STANDBY,
    },
    {
      key: ProgramWizardCurrentProgramId.STANDALONE,
      text: t('program.island'),
      disabled: currentProgram?.id === ProgramWizardCurrentProgramId.STANDALONE,
      nextStep: ProgramWizardStep.CONFIRM,
      operatingMode: ProgramWizardOperatingMode.STANDALONE,
    },
    {
      key: 'peakShave',
      text: t('program.peakShave'),
      nextStep: ProgramWizardStep.PEAKSHAVE_MODE,
    },
    {
      key: ProgramWizardCurrentProgramId.GEN_SET_CYCLE,
      text: t('program.cycling'),
      disabled: currentProgram?.id === ProgramWizardCurrentProgramId.GEN_SET_CYCLE,
      nextStep: ProgramWizardStep.INPUT_SETTINGS,
      operatingMode: ProgramWizardOperatingMode.GEN_SET_CYCLE,
    },
    {
      key: ProgramWizardCurrentProgramId.GEN_SET_NIGHT_MODE,
      text: t('program.loadShare'),
      disabled: currentProgram?.id === ProgramWizardCurrentProgramId.GEN_SET_NIGHT_MODE,
      nextStep: ProgramWizardStep.INPUT_SETTINGS,
      operatingMode: ProgramWizardOperatingMode.LOADSHARE,
    },
    {
      key: ProgramWizardCurrentProgramId.SHUTDOWN,
      text: t('program.shutdown'),
      disabled: currentProgram?.id === ProgramWizardCurrentProgramId.SHUTDOWN,
      nextStep: ProgramWizardStep.CONFIRM,
      operatingMode: ProgramWizardOperatingMode.SHUTDOWN,
    },
  ];

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const option = options.find((o) => o.key === event.target.value);
    if (option) {
      onSelect(option);
    }
  };

  return (
    <>
      <Typography variant="subtitle1" color="text.secondary">
        {t('program.title')}
      </Typography>

      <RadioGroup
        value={selected?.key ?? ''}
        onChange={handleChange}
        sx={{ flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: isMobile ? 1 : 0, mb: 2 }}
      >
        {options.map((option) => (
          <WizardRadioButton key={option.key} value={option.key} text={option.text} disabled={option.disabled} />
        ))}
      </RadioGroup>
    </>
  );
};

export default StepSelectProgram;
